import {
  OrganisationPurchaseRequestProductionPlanCard,
  OrganisationPurchaseRequestSummarySection,
  OrganisationPurchaserSummarySection,
  PurchaseRequestFlowStepper,
} from "@fero/components";
import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import { useTradeSalesStore } from "@fero/stores";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Modal,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { FEROVINUM_CONTACT_URL } from "components/assets/url";
import { useInfoDialog } from "components/context/global-dialog/use-dialog";
import { ScrollToTop } from "components/utils/scroll-to-top/scroll-to-top";
import { LoadingActionButton } from "components/widgets/buttons/loading-action-button/loading-action-button";
import { Link } from "components/widgets/link/link";
import type React from "react";
import { useMemo, useState } from "react";
import type { PurchaseRequestConfirmationData } from "../use-purchase-request-preview";

export interface OrganisationPurchaseRequestConfirmationPageViewProps {
  purchaseRequestData: PurchaseRequestConfirmationData;
  poFileAdded: boolean;
  onCreateAnother: () => void;
  onBack: () => void;
  onConfirm: () => Promise<void>;
}

type Sections =
  | "Purchase request overview"
  | "Production plan"
  | "Purchaser Summary";
export const OrganisationPurchaseRequestConfirmationPageView = ({
  purchaseRequestData,
  poFileAdded,
  onCreateAnother,
  onBack,
  onConfirm,
}: OrganisationPurchaseRequestConfirmationPageViewProps) => {
  const [selectedTab, setSelectedTab] = useState<Sections>(
    "Purchase request overview",
  );
  const { validEstimateNetReceivable, isConfirmed } = purchaseRequestData;
  const hasProductionPlanAttached =
    purchaseRequestData.productionPlan !== undefined;

  return (
    <PortalPageContent
      header={
        <OrganisationPurchaseRequestConfirmationHeader
          purchaseRequest={isConfirmed ? purchaseRequestData : undefined}
          hasProductionPlanAttached={hasProductionPlanAttached}
          purchaserName={purchaseRequestData.purchaserName}
        />
      }
    >
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Tabs value={selectedTab} onChange={(_e, v) => setSelectedTab(v)}>
            <Tab
              label="Purchase request overview"
              value="Purchase request overview"
            />
            {hasProductionPlanAttached && (
              <Tab label="Production plan" value="Production plan" />
            )}
            <Tab label="Purchaser Summary" value="Purchaser Summary" />
          </Tabs>
        </Grid>
        {selectedTab === "Purchase request overview" && (
          <Grid item xs={12}>
            <OverviewSection purchaseRequestData={purchaseRequestData} />
          </Grid>
        )}
        {selectedTab === "Production plan" && (
          <Grid item xs={12}>
            <ProductionPlanSection purchaseRequestData={purchaseRequestData} />
          </Grid>
        )}
        {selectedTab === "Purchaser Summary" && (
          <Grid item xs={12}>
            <OrganisationPurchaserSummarySection
              purchaseRequestData={purchaseRequestData}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <ActionButtons
            isValid={validEstimateNetReceivable}
            onBack={onBack}
            onConfirm={onConfirm}
            isConfirmed={isConfirmed}
            purchaserName={purchaseRequestData.purchaserName}
            poFileAdded={poFileAdded}
            onCreateAnother={onCreateAnother}
          />
        </Grid>
      </Grid>
    </PortalPageContent>
  );
};

interface SectionProps {
  purchaseRequestData: PurchaseRequestConfirmationData;
}

export const OverviewSection = ({ purchaseRequestData }: SectionProps) => {
  const { validEstimateNetReceivable } = purchaseRequestData;
  const state = useMemo(() => {
    if (purchaseRequestData.poFileId) {
      return "purchaserAccepted";
    }
    return "new";
  }, [purchaseRequestData.poFileId]);

  return (
    <Stack spacing={5}>
      {!validEstimateNetReceivable && (
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningAmberOutlinedIcon color="warning" />
          <Typography color="common.error">
            Estimated net receivable amount cannot be 0 or negative. Please
          </Typography>
          <Link color="error" href={FEROVINUM_CONTACT_URL}>
            contact Ferovinum
          </Link>
        </Stack>
      )}
      <OrganisationPurchaseRequestSummarySection
        {...purchaseRequestData}
        summary={{
          ...purchaseRequestData.summary,
          netReceivable: purchaseRequestData.netReceivableAmount,
          additionalMonthlyFees: purchaseRequestData.additionalMonthlyFeeAmount,
          cashAmountToAdvanceUponDelivery:
            purchaseRequestData.advanceCashUponDelivery
              ? (purchaseRequestData.summary?.discountedReceivable
                  ?.amountAdvancedUponDelivery ?? null)
              : null,
          additionalFundingRequested:
            purchaseRequestData.advanceCashUponDelivery,
          purchaserCoversDeliveryCost:
            purchaseRequestData.purchaserCoversDeliveryCost,
          totalAdvanceAmountUponDelivery: "0",
          customerPoRef: purchaseRequestData.customerPoRef ?? null,
          shipmentNumber: purchaseRequestData.shipmentNumber ?? null,
        }}
        isConfirmed={purchaseRequestData.isConfirmed}
        state={state}
        poFileId={purchaseRequestData.poFileId ?? null}
        advanceUponDeliveryPaymentDate={null}
        orgNetReceivablePaymentDate={null}
      />
    </Stack>
  );
};

export const ProductionPlanSection = ({
  purchaseRequestData,
}: SectionProps) => {
  return (
    <Stack spacing={5}>
      {purchaseRequestData.productionPlan?.map((productionPlan) => (
        <OrganisationPurchaseRequestProductionPlanCard
          key={productionPlan.finishedProduct.product.id}
          productionPlan={productionPlan}
          currency={purchaseRequestData.settlementCurrency}
        />
      ))}
    </Stack>
  );
};

const OrganisationPurchaseRequestConfirmationHeader = ({
  purchaseRequest,
  hasProductionPlanAttached,
  purchaserName,
}: {
  purchaseRequest?: PurchaseRequestConfirmationData;
  hasProductionPlanAttached: boolean;
  purchaserName: string;
}) => {
  const { options } = useTradeSalesStore();
  const title = (
    <Stack>
      {options.isEditMode ? (
        <Typography variant={"h4"}>Edit Trade Sale</Typography>
      ) : (
        <Typography variant={"h4"}>
          {purchaseRequest ? "" : "New"} Trade Sale
        </Typography>
      )}
      <Typography>from {purchaserName}</Typography>
    </Stack>
  );

  if (purchaseRequest) {
    return (
      <>
        <PortalPageContentHeader
          useConfirmationTitle
          variant="split"
          title={title}
          right={
            <Stack width={"100%"} textAlign={"right"}>
              <Typography sx={{ fontSize: 12, color: "common.grey5" }}>
                Reference Number
              </Typography>
              <Typography variant="subtitle1Bold">
                {purchaseRequest.purchaseRequestNumber}
              </Typography>
            </Stack>
          }
        />
        <ScrollToTop />
      </>
    );
  }

  return (
    <PortalPageContentHeader
      variant="split"
      title={title}
      right={
        <PurchaseRequestFlowStepper
          activeStep={hasProductionPlanAttached ? 4 : 3}
          withProductionPlan={hasProductionPlanAttached}
        />
      }
    />
  );
};

interface ActionButtonsProps {
  onBack: () => void;
  onConfirm: () => Promise<void>;
  isValid: boolean;
  isConfirmed: boolean;
  purchaserName: string;
  poFileAdded: boolean;
  onCreateAnother: () => void;
}

interface EditConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const EditConfirmationModal: React.FC<EditConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="edit-confirmation-modal"
    >
      <Stack
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: 1,
          p: 4,
          gap: 2,
        }}
      >
        <Typography variant="h6" id="edit-confirmation-modal">
          Are you sure you wish to amend this order
        </Typography>

        <Typography>
          You must manually communicate any relevant changes to the{" "}
          <strong>purchaser</strong> and <strong>storage location</strong>{" "}
          including updates to
        </Typography>

        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>Products</li>
          <li>Quantities</li>
          <li>Delivery date and time</li>
          <li>Special Instructions</li>
        </ul>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 2 }}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingActionButton
            variant="contained"
            loading={loading}
            onClick={async () => {
              setLoading(true);
              await onConfirm();
              setLoading(false);
            }}
          >
            Confirm Amendment
          </LoadingActionButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

const ActionButtons = ({
  onBack,
  onConfirm,
  isConfirmed,
  isValid,
  purchaserName,
  poFileAdded,
  onCreateAnother,
}: ActionButtonsProps) => {
  const [loading, setLoading] = useState(false);
  const [acceptDetails, setAcceptDetails] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { showInfoDialog } = useInfoDialog();
  const { options } = useTradeSalesStore();

  const handleConfirm = async () => {
    if (options.isEditMode) {
      setIsEditModalOpen(true);
      return;
    }
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  const handleModalConfirm = async () => {
    setIsEditModalOpen(false);
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <>
      <Stack direction="row" spacing={2} justifyContent={"flex-end"}>
        {isConfirmed ? (
          <Button onClick={onCreateAnother}>
            Create another sale order for {purchaserName}
          </Button>
        ) : (
          <>
            {poFileAdded && (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={acceptDetails}
                      onClick={() => setAcceptDetails(!acceptDetails)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={0.5}>
                      <Typography>Select to confirm the</Typography>
                      <Link
                        onClick={async (e) => {
                          e.preventDefault();
                          await showInfoDialog({
                            body: (
                              <Stack spacing={2}>
                                <ol>
                                  <li>
                                    The attached purchase order has been made
                                    out to Ferovinum
                                  </li>
                                  <li>
                                    All details entered in this sale order match
                                    what is shown on the uploaded purchase order
                                  </li>
                                </ol>
                              </Stack>
                            ),
                            size: "md",
                          });
                        }}
                      >
                        details
                      </Link>
                      <Typography>are correct</Typography>
                    </Stack>
                  }
                />
              </FormGroup>
            )}
            <Button variant="outlined" onClick={onBack} disabled={loading}>
              Back
            </Button>
            <LoadingActionButton
              variant="contained"
              disabled={!isValid || (poFileAdded && !acceptDetails)}
              onClick={handleConfirm}
            >
              Confirm
            </LoadingActionButton>
          </>
        )}
      </Stack>

      <EditConfirmationModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onConfirm={handleModalConfirm}
      />
    </>
  );
};
